import { ReactComponent as WarningStaticIcon } from '@basqet-dashboard/common/assets/icons/warning-static.svg';
import { Button, Modal } from '@basqet-dashboard/common/components';
import { FileUpload } from '@components/onboarding';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { getRemoteFileByAttachmentField, mapAttachmentMetaByField } from '@utils/attachment-meta';
import { mapComplianceDocs } from '@utils/compliance-mappers';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { AML_MANDATORY_INDUSTRIES, KYB_SAMPLE_DOCS } from '../../shared-constants';
import { isDNFIIndustry } from '../corporate-non-fi-constants';

// ─── Document field configs per account type ─────────────────────────────────

const OptionalLabel = ({ text }) => (
  <>
    {text} <span className="corp-step-form__label-optional">(optional)</span>
  </>
);

OptionalLabel.propTypes = { text: PropTypes.string.isRequired };

const SPECIAL_UPLOAD_EVENTS = {
  scuml_certificate: ANALYTICS_EVENTS.CFI_SCUML_UPLOADED,
  aml_policy_document: ANALYTICS_EVENTS.KYB_AML_POLICY_UPLOADED
};

// ─── Shared doc entries ──────────────────────────────────────────────────────

const CERT_OF_INCORPORATION = {
  key: 'certificate_of_incorporation',
  label: 'Certificate of Incorporation',
  description:
    'Upload certificate issued by CAC or incorporating body of your country. Must be clear and readable',
  sampleTitle: { normal: 'Sample', cursive: 'Certificate of Incorporation' },
  sampleImage: KYB_SAMPLE_DOCS.CERTIFICATE_OF_INCORPORATION,
  required: true,
  errorMsg: 'Certificate of Incorporation is required'
};

const MEMORANDUM = {
  key: 'memorandum_of_association',
  label: 'Memorandum & Articles of Incorporation',
  description: 'Upload full document',
  sampleTitle: { normal: 'Sample', cursive: 'Memorandum Document' },
  sampleImage: KYB_SAMPLE_DOCS.MEMORANDUM_ARTICLES,
  required: true,
  errorMsg: 'Memorandum & Articles of Incorporation is required'
};

const AML_POLICY_DOC = {
  key: 'aml_policy_document',
  label: 'AML Policy Document',
  description: "Upload your company's Anti-Money Laundering policy document",
  sampleTitle: { normal: 'Sample', cursive: 'AML Policy Document' },
  sampleImage: KYB_SAMPLE_DOCS.AML_POLICY,
  required: true,
  errorMsg: 'AML Policy Document is required'
};

// ─── Document field configs per account type ─────────────────────────────────

const FI_DOC_CONFIG = [
  CERT_OF_INCORPORATION,
  MEMORANDUM,
  {
    key: 'operating_license',
    label: 'Valid Operating License',
    description: 'Must be valid & current',
    sampleTitle: { normal: 'Sample', cursive: 'Operating License' },
    sampleImage: KYB_SAMPLE_DOCS.OPERATING_LICENSE,
    required: true,
    errorMsg: 'Valid Operating License is required'
  },
  AML_POLICY_DOC
];

function getNonFIDocConfig(industry) {
  const isDnfi = isDNFIIndustry(industry);
  const needsAmlDoc = AML_MANDATORY_INDUSTRIES.includes(industry);

  const docs = [
    CERT_OF_INCORPORATION,
    MEMORANDUM,
    {
      key: 'operating_license',
      label: <OptionalLabel text="Valid Operating License" />,
      description: 'Must be valid & current',
      sampleTitle: { normal: 'Sample', cursive: 'Operating License' },
      sampleImage: KYB_SAMPLE_DOCS.OPERATING_LICENSE,
      required: false
    }
  ];

  if (isDnfi) {
    docs.push({
      key: 'scuml_certificate',
      label: 'SCUML Certificate',
      description:
        'Upload evidence of SCUML registration. Required for Designated Non-Financial Institutions.',
      sampleTitle: { normal: 'Sample', cursive: 'SCUML Certificate' },
      sampleImage: KYB_SAMPLE_DOCS.SCUML,
      required: true,
      errorMsg: 'SCUML Certificate is required for your industry'
    });
  }

  if (needsAmlDoc) {
    docs.push(AML_POLICY_DOC);
  }

  return docs;
}

const AML_CHECKBOX_TEXT =
  'We confirm that our operations are conducted in compliance with applicable anti-money ' +
  'laundering laws and regulations, and that we have implemented adequate policies, procedures, ' +
  'and controls to detect, prevent, and report money laundering, proliferation financing and ' +
  'terrorist financing activities.';

const FI_CHECKBOX_CONFIG = [{ key: 'confirmed', trackEvent: true }];

function getNonFICheckboxConfig(industry) {
  const needsAmlDoc = AML_MANDATORY_INDUSTRIES.includes(industry);
  const checkboxes = [{ key: 'confirmed', trackEvent: true }];
  if (!needsAmlDoc) {
    checkboxes.push({ key: 'amlConfirmed', text: AML_CHECKBOX_TEXT });
  }
  return checkboxes;
}

const ACCURACY_TEXT =
  'I confirm that all information and documents provided are accurate. Incorrect or incomplete ' +
  'details may delay the review or lead to rejection. Compliance may contact me if ' +
  'clarification is needed.';

// ─── Component ───────────────────────────────────────────────────────────────

const CompanyRegistration = ({
  onComplete,
  initialData,
  kybAccountType = KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
  isLastStep = false,
  industry,
  readOnly = false
}) => {
  const { track } = useAnalytics();
  const { uploadFile, saveSection, submitForReview } = useCompliance();

  const docConfig = useMemo(
    () =>
      kybAccountType === KYB_ACCOUNT_TYPE.CORPORATE_FI
        ? FI_DOC_CONFIG
        : getNonFIDocConfig(industry),
    [kybAccountType, industry]
  );
  const checkboxConfig = useMemo(
    () =>
      kybAccountType === KYB_ACCOUNT_TYPE.CORPORATE_FI
        ? FI_CHECKBOX_CONFIG
        : getNonFICheckboxConfig(industry),
    [kybAccountType, industry]
  );
  const attachmentMetaByField = mapAttachmentMetaByField(initialData?.attachment_meta);

  useTrackOnMount(ANALYTICS_EVENTS.KYB_DOCUMENT_PAGE_VIEWED, {
    kyb_account_type: kybAccountType
  });

  const buildInitialFiles = () => {
    return {
      certificate_of_incorporation: getRemoteFileByAttachmentField(
        attachmentMetaByField,
        'certificate_of_incorporation_attachment_id'
      ),
      memorandum_of_association: getRemoteFileByAttachmentField(
        attachmentMetaByField,
        'memorandum_articles_attachment_id'
      ),
      operating_license: getRemoteFileByAttachmentField(
        attachmentMetaByField,
        'valid_operating_license_attachment_id'
      ),
      aml_policy_document: getRemoteFileByAttachmentField(
        attachmentMetaByField,
        'aml_policy_attachment_id'
      ),
      scuml_certificate: getRemoteFileByAttachmentField(
        attachmentMetaByField,
        'scuml_attachment_id'
      )
    };
  };

  const buildInitialAttachmentIds = () => {
    const initial = {};
    if (initialData?.certificate_of_incorporation_attachment_id) {
      initial.certificate_of_incorporation = initialData.certificate_of_incorporation_attachment_id;
    }
    if (initialData?.memorandum_articles_attachment_id) {
      initial.memorandum_of_association = initialData.memorandum_articles_attachment_id;
    }
    if (initialData?.valid_operating_license_attachment_id) {
      initial.operating_license = initialData.valid_operating_license_attachment_id;
    }
    if (initialData?.aml_policy_attachment_id) {
      initial.aml_policy_document = initialData.aml_policy_attachment_id;
    }
    if (initialData?.scuml_attachment_id) {
      initial.scuml_certificate = initialData.scuml_attachment_id;
    }
    return initial;
  };

  const buildInitialCheckboxes = () => {
    const initial = {};
    checkboxConfig.forEach((cb) => {
      initial[cb.key] = initialData?.[cb.key] || false;
    });
    // Map AML attestation
    if (initialData?.aml_attestation_confirmed) {
      initial.amlConfirmed = initialData.aml_attestation_confirmed;
    }
    return initial;
  };

  const [files, setFiles] = useState(buildInitialFiles);
  const [attachmentIds, setAttachmentIds] = useState(buildInitialAttachmentIds);
  const [fileErrors, setFileErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [checkboxes, setCheckboxes] = useState(buildInitialCheckboxes);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = async (field, file) => {
    if (!file) return;

    setFiles((prev) => ({ ...prev, [field]: file }));
    setFileErrors((prev) => ({ ...prev, [field]: undefined }));

    try {
      const eventName = SPECIAL_UPLOAD_EVENTS[field] || ANALYTICS_EVENTS.KYB_DOC_UPLOADED;
      track(eventName, { kyb_account_type: kybAccountType, doc_type: field });

      const result = await uploadFile(file);
      setAttachmentIds((prev) => ({ ...prev, [field]: result.attachment_id }));
    } catch (error) {
      setFiles((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleDeleteRequest = (field) => {
    setDeleteTarget(field);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    track(ANALYTICS_EVENTS.KYB_DOC_DELETED, {
      kyb_account_type: kybAccountType,
      doc_type: deleteTarget
    });
    setFiles((prev) => ({ ...prev, [deleteTarget]: null }));
    setAttachmentIds((prev) => {
      const next = { ...prev };
      delete next[deleteTarget];
      return next;
    });
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const validateFiles = () => {
    const errs = {};
    docConfig.forEach((doc) => {
      if (doc.required && !attachmentIds[doc.key]) {
        errs[doc.key] = doc.errorMsg;
      }
    });
    setFileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const requiredFilesFilled = docConfig
    .filter((doc) => doc.required)
    .every((doc) => !!attachmentIds[doc.key] || !!files[doc.key]);
  const allCheckboxesChecked = checkboxConfig.every((cb) => checkboxes[cb.key]);
  const canSubmit = allCheckboxesChecked && requiredFilesFilled;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFiles()) return;

    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.KYB_SUBMIT_CLICKED, { kyb_account_type: kybAccountType });

      const apiData = mapComplianceDocs(checkboxes, attachmentIds);

      const result = await saveSection('COMPLIANCE_DOCS', apiData, {
        entryId: initialData?._entryId,
        version: initialData?._version
      });

      if (isLastStep) {
        await submitForReview();
      }

      toast.success('Company Registration & Compliance Docs saved successfully');
      onComplete({
        ...attachmentIds,
        ...checkboxes,
        _entryId: result.entry_id,
        _version: result.version
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save compliance docs:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (key, checked) => {
    setCheckboxes((prev) => ({ ...prev, [key]: checked }));
  };

  return (
    <form className="corp-step-form" onSubmit={handleSubmit}>
      {docConfig.map((doc) => (
        <div key={doc.key} className="corp-step-form__field">
          <FileUpload
            label={doc.label}
            description={doc.description}
            value={files[doc.key]}
            onChange={(file) => handleFileChange(doc.key, file)}
            onDelete={() => handleDeleteRequest(doc.key)}
            onView={() =>
              track(ANALYTICS_EVENTS.KYB_DOC_VIEWED, {
                kyb_account_type: kybAccountType,
                doc_type: doc.key
              })
            }
            sampleTitle={doc.sampleTitle}
            sampleImage={doc.sampleImage}
            maxSizeMB={5}
            error={fileErrors[doc.key]}
            disabled={readOnly}
          />
        </div>
      ))}

      {!readOnly && (
        <>
          {checkboxConfig.map((cb) => (
            <label key={cb.key} className="corp-step-form__confirm-label">
              <input
                type="checkbox"
                className="corp-step-form__confirm-checkbox"
                checked={checkboxes[cb.key]}
                onChange={(e) => {
                  handleCheckboxChange(cb.key, e.target.checked);
                  if (cb.trackEvent && e.target.checked) {
                    track(ANALYTICS_EVENTS.KYB_DOCS_CONFIRMED, {
                      kyb_account_type: kybAccountType
                    });
                  }
                }}
              />
              {cb.text || ACCURACY_TEXT}
            </label>
          ))}

          <Button type="submit" isFull isLoading={isSubmitting} disabled={!canSubmit}>
            Save and Continue
          </Button>
        </>
      )}

      {showDeleteModal && (
        <Modal
          Icon={WarningStaticIcon}
          header={{ name: 'Delete', cursive: 'Document' }}
          onModalClose={() => setShowDeleteModal(false)}>
          <p className="modal__text">Are you sure you want to delete this document?</p>
          <Button isFull variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button isFull onClick={confirmDelete}>
            Yes, continue
          </Button>
        </Modal>
      )}
    </form>
  );
};

CompanyRegistration.propTypes = {
  onComplete: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  kybAccountType: PropTypes.oneOf([
    KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
    KYB_ACCOUNT_TYPE.CORPORATE_FI
  ]),
  isLastStep: PropTypes.bool,
  industry: PropTypes.string,
  readOnly: PropTypes.bool
};

export { CompanyRegistration };
